<template>
    <ManagementListPage class="proceedings-page proceedings-shadcn" title="Impugnações"
        subtitle="Impugnações de crédito e habilitações retardatárias, com dados do credor e reservas vinculadas."
        :result-text="`${dataList.count} impugnações encontradas`" :breadcrumbs="breadcrumbs"
        :summary-cards="layoutSummaryCards" @summary-click="applySummaryFilter">
        <template v-slot:actions>
                <v-btn outlined color="primary" @click="load(true)" :loading="dataList.loading">
                    <v-icon left small>mdi-refresh</v-icon>Atualizar
                </v-btn>
                <v-btn v-if="$check(user, ['proceedings.import'])" outlined color="primary" @click="openImportDialog">
                    <v-icon left small>mdi-file-upload-outline</v-icon>Importar
                </v-btn>
                <v-btn color="primary" elevation="0" to="/impugnacoes/novo">
                    <v-icon left small>mdi-plus</v-icon>Incluir impugnação
                </v-btn>
        </template>

        <template v-slot:filters>
            <div class="filter-section">
                <v-form @submit.prevent="applyFilters">
                    <div class="filter-toolbar">
                        <v-text-field v-model="dataList.filter.term" class="filter-search" dense outlined hide-details clearable
                            prepend-inner-icon="mdi-magnify" placeholder="Buscar por processo, requerente, credor ou CPF/CNPJ" />
                        <v-btn outlined color="primary" class="filter-action filter-toggle" @click="showMoreFilters = !showMoreFilters">
                            <v-icon left size="15">mdi-tune-variant</v-icon>
                            Filtros
                            <span v-if="activeFilters.length" class="filter-count">{{ activeFilters.length }}</span>
                            <v-icon right size="15">{{ showMoreFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>
                        <v-btn type="submit" color="primary" elevation="0" class="filter-action filter-submit">
                            <v-icon left size="15">mdi-magnify</v-icon>Buscar
                        </v-btn>
                    </div>
                    <v-expand-transition>
                        <div v-if="showMoreFilters" class="filter-panel mt-3 pt-3">
                            <div class="filter-panel-head"><strong>Filtros avançados</strong><span>Refine as impugnações exibidas na listagem.</span></div>
                            <v-row dense class="filter-grid">
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Tipo do incidente</label><v-select v-model="dataList.filter.type" :items="dataset.proceedingTypesFilter" item-value="id" item-text="name" dense outlined hide-details clearable placeholder="Todos os tipos" /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Status</label><v-select v-model="dataList.filter.workflow_status" :items="dataset.workflowStatuses" item-value="id" item-text="name" dense outlined hide-details clearable placeholder="Todos os status" /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Responsável</label><v-autocomplete v-model="dataList.filter.responsible_admin_id" :items="dataset.adminFilter" item-value="id" item-text="name" dense outlined hide-details clearable placeholder="Todos os responsáveis" /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Natureza do pedido</label><v-select v-model="dataList.filter.request_kind" :items="dataset.requestKinds" item-value="id" item-text="name" dense outlined hide-details clearable placeholder="Todas as naturezas" /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Classe</label><v-select v-model="dataList.filter.credit_class" :items="dataset.creditorTypes" item-value="id" item-text="name" dense outlined hide-details clearable placeholder="Todas as classes" /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Reservas</label><v-select v-model="dataList.filter.has_reservation" :items="dataset.reservationFilters" item-value="id" item-text="name" dense outlined hide-details clearable placeholder="Todas as situações" /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Distribuição inicial</label><v-text-field v-model="dataList.filter.distribution_date_from" type="date" dense outlined hide-details /></div></v-col>
                                <v-col cols="12" sm="6" lg="3"><div class="filter-field"><label>Distribuição final</label><v-text-field v-model="dataList.filter.distribution_date_to" type="date" dense outlined hide-details /></div></v-col>
                            </v-row>
                            <div class="filter-footer"><span>{{ activeFilters.length ? `${activeFilters.length} filtro(s) ativo(s)` : 'Nenhum filtro adicional aplicado' }}</span><v-btn v-if="activeFilters.length" text small color="primary" @click="clearFilters">Limpar filtros</v-btn></div>
                        </div>
                    </v-expand-transition>
                </v-form>
            </div>
        </template>

        <template v-slot:table>
            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" @update:options="load" :loading="dataList.loading" disable-sort
                no-data-text="Nenhuma impugnação foi encontrada" loading-text="Carregando impugnações..."
                class="management-list-table proceedings-table" :footer-props="dataList.footerOptions" @click:row="viewItem">
                <template v-slot:[`item.process_number`]="{ item }">
                    <div class="process-cell">
                        <strong>{{ item.process_number }}</strong>
                        <span>{{ typeLabel(item.type) }}</span>
                    </div>
                </template>
                <template v-slot:[`item.creditor`]="{ item }">
                    <div class="process-cell">
                        <strong>{{ item.primary_creditor ? item.primary_creditor.name : 'Credor não vinculado' }}</strong>
                        <span>{{ item.primary_creditor ? formatRegistration(item.primary_creditor.registration) : '—' }}</span>
                    </div>
                </template>
                <template v-slot:[`item.request_kind`]="{ item }">
                    <div class="process-cell">
                        <strong>{{ requestKindLabel(item.request_kind) }}</strong>
                        <span>{{ primaryClassLabel(item) }}</span>
                    </div>
                </template>
                <template v-slot:[`item.credit_totals`]="{ item }">
                    <div class="amount-lines">
                        <span v-for="line in amountLines(item.credit_totals)" :key="line">{{ line }}</span>
                        <span v-if="!amountLines(item.credit_totals).length">—</span>
                    </div>
                </template>
                <template v-slot:[`item.distribution_date`]="{ item }">{{ formatDate(item.distribution_date) }}</template>
                <template v-slot:[`item.responsible_admin`]="{ item }">
                    <div v-if="item.responsibleAdmin || item.responsible_admin" class="responsible-cell">
                        <span class="avatar-mini">{{ initials((item.responsibleAdmin || item.responsible_admin).name) }}</span>
                        {{ (item.responsibleAdmin || item.responsible_admin).name }}
                    </div>
                    <span v-else class="subtle-warning"><v-icon x-small>mdi-account-alert-outline</v-icon>Sem responsável</span>
                </template>
                <template v-slot:[`item.reservations`]="{ item }">
                    <div v-if="item.reservation_count" class="process-cell">
                        <strong>{{ item.reservation_count }} {{ item.reservation_count === 1 ? 'reserva' : 'reservas' }}</strong>
                        <span>{{ amountLines(item.reservation_totals).join(' · ') }}</span>
                    </div>
                    <span v-else class="grey--text">Sem reserva</span>
                </template>
                <template v-slot:[`item.workflow_status`]="{ item }">
                    <div class="status-cell">
                        <span class="status-indicator"><span class="status-dot" :style="{ backgroundColor: statusColor(item.workflow_status) }" />{{ statusLabel(item.workflow_status) }}</span>
                        <v-tooltip bottom v-if="item.missing_fields && item.missing_fields.length">
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="status-note">{{ item.missing_fields.length }} dado{{ item.missing_fields.length > 1 ? 's' : '' }} pendente{{ item.missing_fields.length > 1 ? 's' : '' }}</span>
                            </template>
                            <span>{{ item.missing_fields.join(', ') }}</span>
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex" @click.stop>
                        <v-btn icon small @click="copyProcess(item.process_number)"><v-icon small>mdi-content-copy</v-icon></v-btn>
                        <v-btn icon small @click="viewItem(item)"><v-icon small color="primary">mdi-chevron-right</v-icon></v-btn>
                    </div>
                </template>
            </v-data-table>
        </template>

        <v-dialog v-model="importDialog" max-width="720" content-class="proceedings-shadcn-dialog" persistent>
            <v-card>
                <v-card-title class="dialog-header">
                    <div><span>Importar impugnações</span><small>Selecione o caso e valide a planilha antes de confirmar a importação.</small></div>
                    <v-btn icon small aria-label="Fechar" @click="closeImportDialog"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="importFormRef">
                        <v-row><v-col cols="12" md="7"><v-autocomplete v-model="importForm.client_id" :items="dataset.clients" item-value="id" item-text="name" label="Caso" outlined dense :rules="[rules.required]" /></v-col>
                        <v-col cols="12" md="5"><v-text-field v-model="importForm.sheet" label="Aba da planilha" outlined dense :rules="[rules.required]" /></v-col></v-row>
                        <v-file-input v-model="importForm.file" accept=".xlsx,.xls" label="Planilha Excel" outlined dense :rules="[rules.required]" />
                    </v-form>
                    <v-alert v-if="importReport" type="info" text>Simulação: {{ importReport.created || 0 }} registros serão criados, {{ importReport.invalid || 0 }} são inválidos.</v-alert>
                </v-card-text>
                <v-card-actions><v-spacer /><v-btn text @click="closeImportDialog">Cancelar</v-btn><v-btn outlined color="primary" @click="runImport(false)" :loading="importLoading">Simular</v-btn><v-btn color="primary" @click="runImport(true)" :disabled="!importReport" :loading="importLoading">Importar</v-btn></v-card-actions>
            </v-card>
        </v-dialog>
    </ManagementListPage>
</template>

<script>
import axios from "axios";
import proceedingTypes from "@/common/data/proceeding-types.data";
import creditorTypes from "@/common/data/creditor-types.data";
import currencyTypes from "@/common/data/currency-types.data";

export default {
    name: "ProceedingList",
    data: () => ({
        user: null,
        showMoreFilters: false,
        importDialog: false,
        importLoading: false,
        importReport: null,
        importForm: { client_id: null, sheet: "Prazos Incidentes", file: null },
        rules: { required: value => !!value || "Campo obrigatório" },
        breadcrumbs: [{ text: "Gestão", to: "/home" }, { text: "Impugnações", disabled: true }],
        dataset: {
            clients: [], admins: [], adminFilter: [], proceedingTypes, proceedingTypesFilter: proceedingTypes,
            creditorTypes, currencyTypes,
            workflowStatuses: [
                { id: "pending", name: "Pendente" }, { id: "in_analysis", name: "Em análise" },
                { id: "awaiting_information", name: "Aguardando informação" }, { id: "completed", name: "Concluída" },
                { id: "closed", name: "Encerrada" },
            ],
            requestKinds: [{ id: "inclusion", name: "Inclusão de crédito" }, { id: "change", name: "Alteração de crédito" }],
            reservationFilters: [{ id: true, name: "Com reserva" }, { id: false, name: "Sem reserva" }],
        },
        dataList: {
            filter: { term: null, type: null, workflow_status: null, responsible_admin_id: null, request_kind: null, credit_class: null, has_reservation: null, distribution_date_from: null, distribution_date_to: null },
            headers: [
                { text: "Processo", value: "process_number" }, { text: "Requerente", value: "creditor" },
                { text: "Natureza / classe", value: "request_kind" }, { text: "Valor discutido", value: "credit_totals" },
                { text: "Distribuição", value: "distribution_date" }, { text: "Responsável", value: "responsible_admin" },
                { text: "Reservas", value: "reservations" }, { text: "Status", value: "workflow_status" },
                { text: "", value: "actions", sortable: false },
            ],
            data: [], count: 0, summary: {}, loading: false,
            options: { page: 1, itemsPerPage: 25 },
            footerOptions: { itemsPerPageText: "Itens por página:", itemsPerPageOptions: [25, 50, 100], pageText: "{0}-{1} de {2}" },
        },
    }),
    computed: {
        summaryCards() {
            const s = this.dataList.summary || {};
            return [
                { key: "pending", label: "Pendentes", value: s.pending || 0, color: "neutral", filter: "pending" },
                { key: "analysis", label: "Em análise", value: s.in_analysis || 0, color: "primary", filter: "in_analysis" },
                { key: "awaiting", label: "Aguardando informação", value: s.awaiting_information || 0, color: "warning", filter: "awaiting_information" },
                { key: "reserved", label: "Com reserva", value: s.with_active_reservation || 0, color: "purple", reservation: true },
                { key: "completed", label: "Concluídas", value: s.completed || 0, color: "success", filter: "completed" },
            ];
        },
        layoutSummaryCards() {
            return this.summaryCards.map(card => ({
                ...card,
                tone: card.color,
                active: card.reservation
                    ? this.dataList.filter.has_reservation === true
                    : this.dataList.filter.workflow_status === card.filter,
            }));
        },
        activeFilters() {
            const f = this.dataList.filter;
            const values = [];
            if (f.term) values.push({ key: "term", label: `Busca: ${f.term}` });
            if (f.type) values.push({ key: "type", label: `Tipo: ${this.typeLabel(f.type)}` });
            if (f.workflow_status) values.push({ key: "workflow_status", label: `Status: ${this.statusLabel(f.workflow_status)}` });
            if (f.responsible_admin_id !== null && f.responsible_admin_id !== undefined) values.push({ key: "responsible_admin_id", label: `Responsável: ${this.adminLabel(f.responsible_admin_id)}` });
            if (f.request_kind) values.push({ key: "request_kind", label: this.requestKindLabel(f.request_kind) });
            if (f.credit_class) values.push({ key: "credit_class", label: `Classe: ${this.enumLabel(this.dataset.creditorTypes, f.credit_class)}` });
            if (f.has_reservation !== null && f.has_reservation !== undefined) values.push({ key: "has_reservation", label: f.has_reservation ? "Com reserva" : "Sem reserva" });
            if (f.distribution_date_from) values.push({ key: "distribution_date_from", label: `Desde ${this.formatDate(f.distribution_date_from)}` });
            if (f.distribution_date_to) values.push({ key: "distribution_date_to", label: `Até ${this.formatDate(f.distribution_date_to)}` });
            return values;
        },
        activeFilterSummary() {
            return this.activeFilters.map(filter => filter.label).join(" · ");
        },
    },
    created() { this.user = JSON.parse(localStorage.getItem("user")); this.loadSupportingData(); this.load(); },
    methods: {
        async loadSupportingData() {
            try {
                const [clients, admins] = await Promise.all([
                    axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } }),
                    axios.get("/api/admin/list", { params: { start: 1, limit: 100000, search: {} } }),
                ]);
                this.dataset.clients = clients.data.content.data || [];
                this.dataset.admins = (admins.data.content.data || []).sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.adminFilter = [{ id: -1, name: "Sem responsável" }, ...this.dataset.admins];
            } catch (error) { this.notifyError(error); }
        },
        async load(resetPage) {
            if (resetPage === true) this.dataList.options.page = 1;
            this.dataList.loading = true;
            const search = {};
            Object.keys(this.dataList.filter).forEach(key => {
                const value = this.dataList.filter[key];
                if (value !== null && value !== undefined && value !== "") search[key] = value;
            });
            try {
                const result = await axios.get("/api/admin/proceedings", { params: { page: this.dataList.options.page, limit: this.dataList.options.itemsPerPage, search: JSON.stringify(search) } });
                const content = result.data.content;
                this.dataList.data = content.data || [];
                this.dataList.count = content.paging ? content.paging.total : 0;
                this.dataList.summary = content.summary || {};
            } catch (error) { this.notifyError(error); } finally { this.dataList.loading = false; }
        },
        applyFilters() { this.load(true); },
        applySummaryFilter(card) {
            if (card.reservation) {
                this.dataList.filter.has_reservation = this.dataList.filter.has_reservation === true ? null : true;
                this.dataList.filter.workflow_status = null;
            } else {
                this.dataList.filter.workflow_status = this.dataList.filter.workflow_status === card.filter ? null : card.filter;
                this.dataList.filter.has_reservation = null;
            }
            this.load(true);
        },
        clearFilter(key) { this.dataList.filter[key] = null; this.load(true); },
        clearFilters() { Object.keys(this.dataList.filter).forEach(key => { this.dataList.filter[key] = null; }); this.load(true); },
        viewItem(item) { this.$router.push(`/impugnacoes/${item.id}`); },
        typeLabel(value) { return this.enumLabel(this.dataset.proceedingTypes, value); },
        requestKindLabel(value) { return value === "inclusion" ? "Inclusão de crédito" : value === "change" ? "Alteração de crédito" : "Não informado"; },
        statusLabel(value) { const status = this.dataset.workflowStatuses.find(item => item.id === value); return status ? status.name : "Pendente"; },
        statusColor(value) { return { pending: "#8a90a3", in_analysis: "#3f4389", awaiting_information: "#c3832c", completed: "#358c5a", closed: "#6e7486" }[value] || "#8a90a3"; },
        enumLabel(items, value) { const item = items.find(entry => Number(entry.id) === Number(value)); return item ? item.name : "Não informada"; },
        adminLabel(value) { if (Number(value) === -1) return "Sem responsável"; const admin = this.dataset.admins.find(item => Number(item.id) === Number(value)); return admin ? admin.name : "—"; },
        primaryClassLabel(item) { const creditor = item.primary_creditor; const credit = creditor && creditor.credits && creditor.credits[0]; return credit ? this.enumLabel(this.dataset.creditorTypes, credit.class) : "Classe não informada"; },
        currencySymbol(id) { const item = this.dataset.currencyTypes.find(entry => Number(entry.id) === Number(id)); return item && item.currencyFormat ? item.currencyFormat : Number(id) === 2 ? "US$" : Number(id) === 3 ? "€" : "R$"; },
        amountLines(totals) { return Object.keys(totals || {}).map(currency => `${this.currencySymbol(currency)} ${Number(totals[currency]).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`); },
        formatRegistration(value) { const digits = String(value || "").replace(/\D/g, ""); if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"); if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5"); return value || "—"; },
        formatDate(value) { return value ? this.$moment(value).format("DD/MM/YYYY") : "—"; },
        formatRelative(value) { return value ? this.$moment(value).fromNow() : "—"; },
        initials(name) { return String(name || "").split(" ").filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase(); },
        copyProcess(value) { navigator.clipboard.writeText(value); this.$dialog.notify.info("Número do processo copiado", { position: "top-right", timeout: 1500 }); },
        notifyError(error) { this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 }); },
        openImportDialog() { this.importDialog = true; this.importReport = null; },
        closeImportDialog() { if (!this.importLoading) this.importDialog = false; },
        async runImport(commit) {
            if (!this.$refs.importFormRef.validate()) return;
            this.importLoading = true;
            const payload = new FormData(); payload.append("file", this.importForm.file); payload.append("client_id", this.importForm.client_id); payload.append("sheet", this.importForm.sheet); payload.append("force_type", 3); payload.append("commit", commit ? "1" : "0");
            try { const response = await axios.post("/api/admin/proceedings/import", payload); this.importReport = response.data.content; if (commit) { this.importDialog = false; this.load(true); } }
            catch (error) { this.notifyError(error); } finally { this.importLoading = false; }
        },
    },
};
</script>

<style lang="scss" scoped>
.proceedings-page { background: #f6f7fa; min-height: 100%; }
.content-card { border: 1px solid #e1e4ec; border-radius: 12px !important; background: #fff; }
.filter-section { border-bottom: 0; padding: 0 !important; }
.filter-toolbar { align-items: center; display: flex; gap: 8px; min-height: 40px; }
.filter-search { flex: 0 1 560px; min-width: 280px; }
.filter-action { flex: 0 0 auto; }
.filter-count { align-items: center; background: #eef2f6; border-radius: 999px; color: #334155; display: inline-flex; font-size: 9px; height: 16px; justify-content: center; margin-left: 6px; min-width: 16px; padding: 0 4px; }
.filter-panel { border-top: 1px solid #eaecf1; }
.filter-panel-head { margin-bottom: 10px; }
.filter-panel-head strong, .filter-panel-head span { display: block; }
.filter-panel-head strong { color: #334155; font-size: 11px; }
.filter-panel-head span { color: #64748b; font-size: 9px; margin-top: 2px; }
.filter-grid { margin: -5px; }
.filter-grid > .col { padding: 5px; }
.filter-field { height: 100%; }
.filter-field > label { color: #334155; display: block; font-size: 10px; font-weight: 700; line-height: 18px; margin-bottom: 6px; }
.filter-footer { align-items: center; color: #64748b; display: flex; font-size: 10px; justify-content: space-between; min-height: 30px; margin-top: 2px; }
.filter-toolbar ::v-deep .v-input__slot { border-radius: 6px !important; min-height: 40px !important; }
.filter-field ::v-deep .v-input__slot { border-radius: 6px !important; min-height: 44px !important; }
.filter-toolbar ::v-deep input,
.filter-toolbar ::v-deep input::placeholder,
.filter-field ::v-deep input,
.filter-field ::v-deep input::placeholder,
.filter-field ::v-deep .v-select__selection,
.filter-field ::v-deep .v-select__selection--comma { font-family: "Plus Jakarta Sans", "Roboto", sans-serif !important; font-size: 11px !important; font-weight: 400 !important; letter-spacing: 0 !important; line-height: 1.45 !important; }
.filter-toolbar ::v-deep input::placeholder,
.filter-field ::v-deep input::placeholder { color: #94a3b8 !important; opacity: 1 !important; }
.filter-toolbar ::v-deep > .v-btn { align-self: center; border-radius: 6px; box-shadow: none !important; font-size: 11px; font-weight: 600; height: 36px !important; letter-spacing: 0; min-height: 36px; min-width: 0; padding: 0 16px !important; text-transform: none; }
.filter-toolbar ::v-deep > .filter-toggle { background: #fff !important; border-color: #cbd5e1 !important; color: #334155 !important; }
.filter-toolbar ::v-deep > .filter-toggle:hover { background: #f8fafc !important; border-color: #94a3b8 !important; }
.filter-toolbar ::v-deep > .v-btn .v-btn__content { align-items: center; display: inline-flex; height: 100%; justify-content: center; line-height: 1; }
.filter-toolbar ::v-deep > .v-btn .v-icon { align-items: center; display: inline-flex; flex: 0 0 15px; height: 15px; justify-content: center; line-height: 15px; width: 15px; }
.filter-toolbar ::v-deep > .v-btn .v-icon--left { margin-left: -1px; margin-right: 6px; }
.filter-toolbar ::v-deep > .v-btn .v-icon--right { margin-left: 5px; margin-right: -2px; }
.filter-toolbar ::v-deep .v-input__prepend-inner { align-self: center; margin-top: 0 !important; }
.filter-toolbar ::v-deep .v-input__append-inner { align-self: center; margin-top: 0 !important; }
.process-cell, .amount-lines { display: flex; flex-direction: column; gap: 3px; min-width: 140px; }
.process-cell strong { color: #0f172a; font-size: 11px; font-weight: 700; line-height: 1.4; }
.process-cell span, .amount-lines span { color: #64748b; font-size: 9px; line-height: 1.45; }
.responsible-cell { display: flex; align-items: center; gap: 7px; min-width: 145px; }
.avatar-mini { width: 25px; height: 25px; border-radius: 50%; background: #ececfa; color: #353778; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; }
.status-indicator { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; color: #334155; font-size: 10px; font-weight: 650; }
.status-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.status-cell { display: flex; flex-direction: column; gap: 4px; min-width: 125px; }
.status-note { color: #9a6e20; cursor: help; font-size: 10px; padding-left: 15px; }
.subtle-warning { display: inline-flex; align-items: center; gap: 4px; color: #8a651e; font-size: 11px; white-space: nowrap; }
.proceedings-table ::v-deep tbody tr { cursor: pointer; }
@media (max-width: 1264px) {
    .proceedings-table { overflow-x: auto; }
    .filter-summary { display: none; }
}
@media (max-width: 720px) {
    .filter-toolbar { align-items: stretch; flex-wrap: wrap; }
    .filter-search { flex: 1 1 100%; min-width: 100%; }
    .filter-toolbar .v-spacer { display: none; }
}
</style>
